import { useState } from "react";
import { useNavigate } from "react-router";
import { BottomCTA, Checkbox, IconChevronRightLine, TopNavigation } from "@hamlak/ui";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import type { ConsentCode, ConsentItem } from "@/api/types";
import { CONSENT_DETAIL_LINKS, LINKS } from "@/config/links";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import { OnboardingCloseButton } from "./OnboardingCloseButton";
import "./auth.css";

type Row = { code: ConsentCode; label: string; required: boolean; url: string | null };

/** 필수 3(tos·privacy·ai) + 선택 2(marketing·night_marketing, 시안 591:76). 링크(노션, config/links)는 외부 브라우저로. URL 없는 행은 chevron 없음 */
const ROWS: Row[] = [
  { code: "tos", label: t("terms.tos"), required: true, url: LINKS.terms },
  { code: "privacy", label: t("terms.privacy"), required: true, url: LINKS.privacy },
  { code: "ai", label: t("terms.ai"), required: true, url: CONSENT_DETAIL_LINKS.ai },
  { code: "marketing", label: t("terms.marketing"), required: false, url: CONSENT_DETAIL_LINKS.marketing },
  { code: "night_marketing", label: t("terms.nightMarketing"), required: false, url: CONSENT_DETAIL_LINKS.nightMarketing },
];

const allOf = (v: boolean) => Object.fromEntries(ROWS.map((r) => [r.code, v])) as Record<ConsentCode, boolean>;

export const REQUIRED_CONSENTS = ROWS.filter((r) => r.required).map((r) => r.code);

/** F01-03 약관. 필수 3 체크 시 CTA, POST /api/me/consents → /birth-date. 체크 상태는 라우트 로컬. */
export function TermsPage() {
  const navigate = useNavigate();
  const setFlags = useAuthStore((s) => s.setFlags);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [agreed, setAgreed] = useState<Record<ConsentCode, boolean>>(() => allOf(false));
  const [pending, setPending] = useState(false);

  const allChecked = ROWS.every((r) => agreed[r.code]);
  const canSubmit = REQUIRED_CONSENTS.every((c) => agreed[c]) && !pending;

  const toggleAll = (v: boolean) => setAgreed(allOf(v));

  const submit = async () => {
    if (!canSubmit) return;
    setPending(true);
    try {
      const body: ConsentItem[] = ROWS.map((r) => ({ code: r.code, agreed: agreed[r.code] }));
      await apiFetch<void>(ep.meConsents, { method: "POST", body });
      setFlags({ consentsRequired: false });
      navigate("/birth-date", { replace: true });
    } catch (e) {
      // 오프라인·오류: 체크 유지 + Snackbar
      showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setPending(false);
    }
  };

  return (
    <main className="page auth terms">
      {/* 시안 44:868은 제목 없는 nav — 큰 제목이 화면 제목을 겸한다 */}
      <TopNavigation title="" aria-label={t("page.terms")} left={null} right={<OnboardingCloseButton />} />
      <h1 className="auth__title">{t("terms.title")}</h1>
      <div className="auth__form">
        <div className="consent__all">
          <Checkbox size="large" checked={allChecked} onCheckedChange={toggleAll}>
            {t("terms.all")}
          </Checkbox>
        </div>
        <div className="consent__list">
          {ROWS.map((row) => {
            const url = row.url;
            return (
              <div key={row.code} className="consent__row">
                <Checkbox checked={agreed[row.code]} onCheckedChange={(v) => setAgreed((s) => ({ ...s, [row.code]: v }))}>
                  {row.required ? t("terms.required") : t("terms.optional")} {row.label}
                </Checkbox>
                {url && (
                  <button type="button" className="consent__link" aria-label={t("terms.view", { name: row.label })} onClick={() => void platform.browser.open(url)}>
                    <IconChevronRightLine size={16} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
      <BottomCTA primary={{ children: t("terms.cta"), disabled: !canSubmit, loading: pending, onClick: () => void submit() }} />
    </main>
  );
}
